import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { BookOpen, Award, Bookmark, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/DashboardLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        {/* Welcome Section */}
        <div data-testid="dashboard-welcome" className="bg-white rounded-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome to TopMarks</h1>
          <p className="text-slate-600">Your exam preparation dashboard - everything you need to score maximum marks</p>
        </div>

        {/* Stats Grid - Bento Style */}
        <div className="grid md:grid-cols-3 gap-6">
          <div data-testid="stat-questions" className="bg-white rounded-lg border border-slate-200 p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-900" />
              </div>
              <span className="text-3xl font-bold text-blue-900">{stats?.total_questions || 0}</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Questions Answered</h3>
            <p className="text-sm text-slate-600 mt-1">Total questions you've asked</p>
          </div>

          <div data-testid="stat-bookmarks" className="bg-white rounded-lg border border-slate-200 p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-50 rounded-md flex items-center justify-center">
                <Bookmark className="w-6 h-6 text-yellow-600" />
              </div>
              <span className="text-3xl font-bold text-yellow-600">{stats?.total_bookmarks || 0}</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Bookmarks</h3>
            <p className="text-sm text-slate-600 mt-1">Saved for quick revision</p>
          </div>

          <div data-testid="stat-tests" className="bg-white rounded-lg border border-slate-200 p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-50 rounded-md flex items-center justify-center">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-3xl font-bold text-green-600">{stats?.total_tests || 0}</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Practice Tests</h3>
            <p className="text-sm text-slate-600 mt-1">Tests generated for practice</p>
          </div>
        </div>

        {/* Subject Breakdown */}
        {stats?.subject_breakdown && Object.keys(stats.subject_breakdown).length > 0 && (
          <div data-testid="subject-breakdown" className="bg-white rounded-lg border border-slate-200 p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Subject-wise Progress
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(stats.subject_breakdown).map(([subject, count]) => (
                <div key={subject} className="p-4 bg-slate-50 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{subject}</span>
                    <span className="text-2xl font-bold text-blue-900">{count}</span>
                  </div>
                  <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-900"
                      style={{ width: `${Math.min((count / (stats.total_questions || 1)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            data-testid="quick-action-ask"
            className="bg-blue-900 rounded-lg p-8 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate('/ask')}
          >
            <BookOpen className="w-10 h-10 text-white mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Ask a Question</h3>
            <p className="text-blue-100 mb-4">Get instant exam-style answers for any question</p>
            <Button 
              data-testid="ask-question-btn"
              className="bg-white text-blue-900 hover:bg-slate-100"
            >
              Start Now <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div 
            data-testid="quick-action-practice"
            className="bg-white rounded-lg p-8 border border-slate-200 cursor-pointer card-hover"
            onClick={() => navigate('/practice-tests')}
          >
            <Award className="w-10 h-10 text-blue-900 mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Practice Tests</h3>
            <p className="text-slate-600 mb-4">Generate unlimited practice tests for any subject</p>
            <Button 
              data-testid="practice-test-btn"
              variant="outline"
              className="border-blue-900 text-blue-900 hover:bg-blue-50"
            >
              Generate Test <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div 
            data-testid="quick-action-tips"
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-8 border border-green-200 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => window.open('/quick-tips', '_blank')}
          >
            <TrendingUp className="w-10 h-10 text-green-900 mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Quick Tips</h3>
            <p className="text-slate-600 mb-4">Expert tips to score maximum marks</p>
            <Button 
              data-testid="tips-btn"
              variant="outline"
              className="border-green-900 text-green-900 hover:bg-green-50"
            >
              View Tips <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Recent Activity */}
        {stats?.recent_activity && stats.recent_activity.length > 0 && (
          <div data-testid="recent-activity" className="bg-white rounded-lg border border-slate-200 p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Recent Activity
            </h3>
            <div className="space-y-4">
              {stats.recent_activity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{activity.subject}</p>
                      <p className="text-sm text-slate-600">
                        <span className="marks-badge bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          {activity.marks}M
                        </span>
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
